import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'

/**
 * Success page shown after successful Stripe Checkout
 *
 * This page is displayed when a customer completes payment.
 * It includes the session ID from Stripe and next steps for the customer.
 */
export default function Success() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-carrot-50 via-white to-leaf-50">
      <div className="container py-20">
        <div
          className={`max-w-2xl mx-auto text-center transition-all duration-1000 ease-apple ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Success Icon */}
          <div className="mb-8 flex justify-center">
            <div className="w-24 h-24 rounded-full bg-leaf-500 flex items-center justify-center shadow-glow animate-scale-in">
              <svg
                className="w-14 h-14 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-5xl md:text-6xl font-bold text-ink-800 tracking-tight mb-6 animate-fade-in">
            Payment Successful!
          </h1>

          <p className="text-xl md:text-2xl text-ink-600 mb-8 animate-fade-in" style={{ animationDelay: '100ms' }}>
            Thank you for choosing AdKarrot. Your growth journey starts now.
          </p>

          {/* Details Card */}
          <div
            className="card p-8 mb-12 animate-slide-up"
            style={{ animationDelay: '200ms' }}
          >
            <h2 className="text-2xl font-bold text-ink-800 mb-4">
              What happens next?
            </h2>

            <ul className="space-y-4 text-left text-ink-700">
              <li className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-carrot-500 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold">Email Confirmation</p>
                  <p className="text-sm text-ink-600 mt-1">
                    You'll receive a receipt at your email address within a few minutes.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-carrot-500 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold">We'll Reach Out</p>
                  <p className="text-sm text-ink-600 mt-1">
                    Our team will contact you within 1 business day to schedule your session or onboarding.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-carrot-500 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold">Get Ready to Grow</p>
                  <p className="text-sm text-ink-600 mt-1">
                    Prepare any questions or goals you'd like to discuss. We're excited to help you succeed!
                  </p>
                </div>
              </li>
            </ul>

            {sessionId && (
              <div className="mt-8 pt-6 border-t border-ink-100">
                <p className="text-xs text-ink-500">
                  Transaction ID:{' '}
                  <code className="text-carrot-600 bg-carrot-50 px-2 py-1 rounded font-mono">
                    {sessionId}
                  </code>
                </p>
              </div>
            )}
          </div>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
            style={{ animationDelay: '300ms' }}
          >
            <Link
              to="/"
              className="btn-primary"
            >
              Return to Home
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 rounded-2xl
                       bg-white hover:bg-cloud-100 text-carrot-600 font-semibold
                       border-2 border-carrot-500 hover:border-carrot-600
                       shadow-soft hover:shadow-card-hover
                       transition-all duration-300 ease-apple
                       transform hover:scale-[1.02]"
            >
              Contact Us
            </Link>
          </div>

          {/* Help Text */}
          <p className="mt-12 text-sm text-ink-500 animate-fade-in" style={{ animationDelay: '400ms' }}>
            Have questions?{' '}
            <a
              href="mailto:support@adkarrot.com"
              className="text-carrot-600 hover:text-carrot-700 underline underline-offset-2"
            >
              Email us at support@adkarrot.com
            </a>{' '}
            or call{' '}
            <a
              href="tel:+13193436094"
              className="text-carrot-600 hover:text-carrot-700 underline underline-offset-2"
            >
              319-343-6094
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
